/**
 * Region schedule (phone) — a terse list of the region's workshops, the next
 * 14 days first and a month further each time the teacher asks. The teacher's
 * own rows get a tag naming their part (Teaching, Assisting, Training) and the
 * ring, and open; everyone else's are inert.
 */
import { useNavigate } from 'react-router-dom';

import {
  ShowCancelledToggle,
  WorkshopList,
  WorkshopsAheadFooter,
  formatBlockDay,
  useCancelledFilter,
  withoutCancelled,
} from '../../primitives';
import { BucketError, BucketLoading } from '../../regional/BucketStates';
import { teacherChipDetailUrl } from '../../workshopDetailUrlFor';
import { remainingThisWeek } from '../my-week';
import { regionScheduleErrorMessage } from '../region-schedule-error';
import { useRegionScheduleScope } from '../use-region-schedule-scope';
import { useRegionWorkshopsAhead } from '../use-region-workshops-ahead';

export default function TeacherRegionSchedulePhone() {
  const navigate = useNavigate();
  const { regions, current } = useRegionScheduleScope();

  const blocks = useRegionWorkshopsAhead(current?.id);
  const cancelled = useCancelledFilter(blocks.workshops);

  if (regions.length === 0) {
    return (
      <p className="nb-note" style={{ padding: 16 }}>
        You aren&rsquo;t assigned to any region yet — ask a Regional Director to add you.
      </p>
    );
  }
  if (blocks.isLoading) return <BucketLoading />;
  if (blocks.isFirstLoadError) {
    return <BucketError error={new Error(regionScheduleErrorMessage(blocks.error))} />;
  }

  // A cancelled shift is not one to work, shown or not.
  const mineThisWeek = remainingThisWeek(
    withoutCancelled(blocks.workshops).filter((w) => w.mine),
    new Date(),
  );

  return (
    <div>
      <div className="flex items-center gap-2" style={{ margin: '0 0 10px' }}>
        <p style={{ fontSize: 12.5, color: 'var(--nb-subtle)', margin: 0 }}>
          {mineThisWeek} shift{mineThisWeek === 1 ? '' : 's'} this week
        </p>
        <ShowCancelledToggle
          showing={cancelled.showCancelled}
          count={cancelled.cancelledCount}
          onToggle={cancelled.toggle}
        />
      </div>
      <WorkshopList
        workshops={cancelled.shown}
        size="sm"
        showSales={false}
        showCount={false}
        canOpen={(w) => Boolean(w.mine)}
        onWorkshopClick={(w) => navigate(teacherChipDetailUrl(w))}
        emptyLabel={`No workshops scheduled through ${formatBlockDay(blocks.through)}.`}
      />
      <WorkshopsAheadFooter
        through={blocks.showThrough ? blocks.through : null}
        emptyBlock={blocks.emptyBlock}
        isLoadingMore={blocks.isLoadingMore}
        loadMoreFailed={blocks.isLoadMoreError}
        onLoadMore={blocks.loadMore}
      />
    </div>
  );
}
